import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../services/api';
import Button from '../../common/Button';
import './Followups.css';

interface Note {
    id: number;
    content: string;
    date: string;
    created_at: string;
}

export default function ClinicNotesWidget() {
    const [notes, setNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
    const [editContent, setEditContent] = useState('');

    useEffect(() => {
        loadNotes();
    }, [selectedDate]);

    const loadNotes = async () => {
        setLoading(true);
        try {
            const query = selectedDate ? `?date=${selectedDate}` : '';
            const data = await apiGet(`/api/followups/notes${query}`);
            if (Array.isArray(data)) {
                setNotes(data);
            }
        } catch (e) {
            console.error("Failed to load notes", e);
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;
        try {
            await apiPost('/api/followups/notes', {
                content: newNote,
                date: selectedDate || new Date().toISOString().split('T')[0]
            });
            setNewNote('');
            loadNotes();
        } catch (e) {
            console.error("Failed to add note", e);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete this note?")) return;
        try {
            await apiDelete(`/api/followups/notes/${id}`);
            loadNotes();
        } catch (e) {
            console.error("Failed to delete note", e);
        }
    };

    const startEdit = (note: Note) => {
        setEditingNoteId(note.id);
        setEditContent(note.content);
    };

    const cancelEdit = () => {
        setEditingNoteId(null);
        setEditContent('');
    };

    const saveEdit = async (id: number) => {
        try {
            await apiPut(`/api/followups/notes/${id}`, { content: editContent });
            setEditingNoteId(null);
            loadNotes();
        } catch (e) {
            console.error("Failed to update note", e);
        }
    };

    return (
        <div className="mod-card" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, color: 'var(--mod-text-main)', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>📝 Clinic Notes</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {selectedDate && (
                        <Button variant="secondary" size="sm" onClick={() => { setSelectedDate(''); setShowCalendar(false); }}>
                            Clear Date
                        </Button>
                    )}
                    <Button variant="primary" size="sm" onClick={() => setShowCalendar(!showCalendar)} style={{ background: 'var(--mod-sky)' }}>
                        {showCalendar ? 'Hide Calendar' : '📅 Filter Date'}
                    </Button>
                </div>
            </div>

            {showCalendar && (
                <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ fontSize: '0.9rem', color: 'var(--mod-text-muted)', fontWeight: 600 }}>Select Date:</label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', color: 'var(--mod-text-main)' }}
                    />
                </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Type a new note here..."
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                />
                <Button variant="primary" onClick={handleAddNote}>
                    Add Note
                </Button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {loading && <div style={{ padding: '20px', textAlign: 'center', color: 'var(--mod-text-muted)' }}>Loading notes...</div>}

                {!loading && notes.length === 0 && (
                    <div className="mod-empty-state" style={{ padding: '30px 20px', border: '1px dashed #cbd5e1', background: 'transparent' }}>
                        <p style={{ margin: 0 }}>No notes found for {selectedDate || 'recent history'}.</p>
                    </div>
                )}

                {notes.map(note => (
                    <div key={note.id} style={{
                        background: '#f8fafc',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--mod-text-muted)', background: '#e2e8f0', padding: '4px 8px', borderRadius: '6px', fontWeight: 600 }}>
                                {note.date}
                            </span>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {editingNoteId === note.id ? (
                                    <>
                                        <Button variant="primary" size="sm" onClick={() => saveEdit(note.id)}>Save</Button>
                                        <Button variant="secondary" size="sm" onClick={cancelEdit}>Cancel</Button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => startEdit(note)} style={{ color: 'var(--mod-sky)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>Edit</button>
                                        <button onClick={() => handleDelete(note.id)} style={{ color: 'var(--mod-rose)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>Delete</button>
                                    </>
                                )}
                            </div>
                        </div>

                        {editingNoteId === note.id ? (
                            <textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', minHeight: '80px', fontFamily: 'inherit' }}
                            />
                        ) : (
                            <div style={{ color: 'var(--mod-text-main)', lineHeight: '1.5', fontSize: '0.95rem' }}>{note.content}</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
